/**
 * ClaimFilingStepper
 *
 * Three-step top navigation styled as tabs. Each step shows a trailing
 * circular indicator: an empty circle when incomplete, and a green circle
 * with a check when completed. The active tab uses blue text with a blue
 * underline.
 */
import { Check } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import {
  CoverageCategory,
  CoverageSchema,
  PolicyType,
} from '@/services/types/coverage-types';

import { ClaimFilingStep } from '../hooks/useClaimFilingState';

/** Props for `ClaimFilingStepper`. */
interface ClaimFilingStepperProps {
  /** Current step to highlight. */
  step: ClaimFilingStep;
  /** Navigate to a specific step when a pill is clicked. */
  onGoto: (s: ClaimFilingStep) => void;
  /** Whether required review items have been completed. */
  allRequiredReviewed: boolean;
  /** Step completion derived from tasks (server source of truth). */
  reviewDone?: boolean;
  callDone?: boolean;
  finalDone?: boolean; // deprecated alias, kept for backwards compat
  lorDone?: boolean;
  /** Review & Send LOR step completion (task fully completed, not just under review) */
  reviewSendDone?: boolean;
  ackDone?: boolean;
  /** NA flags per step */
  reviewNA?: boolean;
  callNA?: boolean;
  finalNA?: boolean; // deprecated alias, kept for backwards compat
  lorNA?: boolean;
  ackNA?: boolean;
  /** Optional coverage to render left context (carrier • 1P/3P • policy). */
  coverage?: CoverageSchema;
}

const StepPill: React.FC<{
  label: string;
  active: boolean;
  done?: boolean;
  na?: boolean;
  onClick?: () => void;
}> = ({ label, active, done, na, onClick }) => (
  <button
    type="button"
    className={cn(
      'relative px-2 py-2 text-sm font-medium transition-colors',
      active ? 'text-blue-600' : 'text-zinc-800 hover:text-zinc-900',
    )}
    onClick={onClick}
    aria-current={active ? 'step' : undefined}
  >
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      {done ? (
        <span className="inline-flex items-center gap-1">
          {na && (
            <span className="text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200 rounded px-1.5 py-0.5">
              NA
            </span>
          )}
          <span className="h-5 w-5 rounded-full bg-green-600 text-white flex items-center justify-center">
            <Check className="h-3 w-3" />
          </span>
        </span>
      ) : (
        <span className="h-5 w-5 rounded-full border border-zinc-300 bg-white" />
      )}
    </span>
    {active && (
      <span className="absolute left-0 right-0 -bottom-0.5 h-0.5 bg-blue-600 rounded" />
    )}
  </button>
);

export const ClaimFilingStepper: React.FC<ClaimFilingStepperProps> = ({
  step,
  onGoto,
  allRequiredReviewed,
  reviewDone,
  callDone,
  finalDone,
  lorDone,
  reviewSendDone,
  ackDone,
  reviewNA,
  callNA,
  finalNA,
  lorNA,
  ackNA,
  coverage,
}) => {
  const carrierName = coverage?.carrier?.name || 'Unknown Carrier';
  const categoryLabel = React.useMemo(() => {
    if (coverage?.category === CoverageCategory.FIRST_PARTY) return '1P';
    if (coverage?.category === CoverageCategory.THIRD_PARTY) return '3P';
    return '—';
  }, [coverage?.category]);
  const policyType = coverage?.policy_type || PolicyType.AUTO;
  return (
    <div className="w-full border-b">
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-6">
        <div className="min-w-0 text-sm font-semibold text-zinc-900 truncate">
          {carrierName} • {categoryLabel} • {policyType}
        </div>
        <div className="flex items-center gap-10">
          <StepPill
            label="Review Details"
            active={step === 'review'}
            done={Boolean(reviewDone ?? allRequiredReviewed)}
            na={Boolean(reviewNA)}
            onClick={() => onGoto('review')}
          />
          <StepPill
            label="Open Claim"
            active={step === 'call'}
            done={Boolean(callDone)}
            na={Boolean(callNA)}
            onClick={() => onGoto('call')}
          />
          <StepPill
            label="Write a Letter of Representation"
            active={step === 'lor'}
            done={Boolean(lorDone ?? finalDone)}
            na={Boolean(lorNA ?? finalNA)}
            onClick={() => onGoto('lor')}
          />
          <StepPill
            label="Review & Send LOR"
            active={step === 'review_send'}
            done={Boolean(reviewSendDone ?? finalDone)}
            onClick={() => onGoto('review_send')}
          />
          <StepPill
            label="Acknowledgement"
            active={step === 'ack'}
            done={Boolean(ackDone)}
            na={Boolean(ackNA)}
            onClick={() => onGoto('ack')}
          />
        </div>
      </div>
    </div>
  );
};
